// Standardized content type taxonomy for Nick
// Used across Upload, DocumentManager, and other components

export const CONTENT_TYPES = {
  VIDEO: {
    label: 'Video',
    subtypes: ['YouTube', 'MP4', 'AVI', 'Webinar', 'Tutorial', 'Training'],
    acceptedExtensions: ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm'],
    future: false
  },
  IMAGE: {
    label: 'Image',
    subtypes: ['JPEG', 'PNG', 'GIF', 'SVG', 'Screenshot', 'Diagram'],
    acceptedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp'],
    future: false
  },
  AUDIO: {
    label: 'Audio',
    subtypes: ['MP3', 'WAV', 'OGG', 'FLAC', 'Podcast', 'Recording'],
    acceptedExtensions: ['.mp3', '.wav', '.ogg', '.flac', '.m4a', '.aac'],
    future: true // Not yet implemented
  },
  DOCUMENT: {
    label: 'Document',
    subtypes: ['PDF', 'DOCX', 'PPTX', 'XLSX', 'TXT', 'MD'],
    acceptedExtensions: ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.md', '.rtf'],
    future: false
  },
  WEBSITE: {
    label: 'Website',
    subtypes: ['URL', 'Article', 'Blog Post', 'Documentation'],
    acceptedExtensions: ['.html', '.htm', '.mhtml'],
    future: false
  },
  EMAIL: {
    label: 'Email',
    subtypes: ['EML', 'MSG', 'Text Entry'],
    acceptedExtensions: ['.eml', '.msg'],
    future: false
  }
};

export const SOURCE_TYPES = [
  'Research Paper',
  'Journal Article',
  'Thesis',
  'Dissertation',
  'Technical Report',
  'Whitepaper',
  'Presentation',
  'Meeting Notes',
  'Website',
  'Internal Source',
  'Internal Report',
  'External Source',
  'Training Material',
  'Educational Content',
  'Technical Document'
];

// Helper function to get all content type options for dropdowns
export const getContentTypeOptions = (excludeFuture = true) => {
  return Object.entries(CONTENT_TYPES)
    .filter(([_, config]) => !excludeFuture || !config.future)
    .flatMap(([type, config]) => [
      { value: config.label, label: config.label, isGroup: true },
      ...config.subtypes.map(subtype => ({
        value: subtype,
        label: `  ${subtype}`,
        parent: config.label
      }))
    ]);
};

// Helper to get accepted file extensions for a specific content type
export const getAcceptedExtensions = (contentTypeLabel) => {
  const type = Object.values(CONTENT_TYPES).find(ct => ct.label === contentTypeLabel);
  return type?.acceptedExtensions || [];
};

// Helper to get all accepted extensions for all types
export const getAllAcceptedExtensions = (excludeFuture = true) => {
  return Object.values(CONTENT_TYPES)
    .filter(config => !excludeFuture || !config.future)
    .flatMap(config => config.acceptedExtensions);
};

// Helper to determine content type from file extension
export const getContentTypeFromExtension = (filename) => {
  const ext = filename.toLowerCase().match(/\.[^.]*$/)?.[0];
  if (!ext) return null;

  for (const [type, config] of Object.entries(CONTENT_TYPES)) {
    if (config.acceptedExtensions.includes(ext)) {
      return config.label;
    }
  }
  return null;
};

// Dropzone accept configurations for react-dropzone
export const DROPZONE_ACCEPT = {
  document: {
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt'],
    'text/markdown': ['.md'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/rtf': ['.rtf'],
    'text/html': ['.html', '.htm'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/bmp': ['.bmp'],
    'image/tiff': ['.tiff', '.tif'],
    'image/webp': ['.webp']
  },
  email: {
    'message/rfc822': ['.eml'],
    'application/vnd.ms-outlook': ['.msg']
  },
  video: {
    'video/mp4': ['.mp4'],
    'video/x-msvideo': ['.avi'],
    'video/quicktime': ['.mov'],
    'video/x-ms-wmv': ['.wmv'],
    'video/x-flv': ['.flv'],
    'video/webm': ['.webm']
  },
  image: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/svg+xml': ['.svg'],
    'image/webp': ['.webp'],
    'image/bmp': ['.bmp']
  }
};

// Badge color classes for DocumentManager
export const getBadgeClass = (contentType) => {
  const typeMap = {
    // Documents
    'PDF': 'doc-badge--pdf',
    'DOCX': 'doc-badge--document',
    'PPTX': 'doc-badge--presentation',
    'XLSX': 'doc-badge--spreadsheet',
    'TXT': 'doc-badge--text',
    'MD': 'doc-badge--text',
    
    // Websites
    'Website': 'doc-badge--website',
    'URL': 'doc-badge--website',
    'Article': 'doc-badge--website',
    'Blog Post': 'doc-badge--website',
    
    // Email
    'Email': 'doc-badge--email',
    'EML': 'doc-badge--email',
    'MSG': 'doc-badge--email',
    
    // Video
    'Video': 'doc-badge--video',
    'YouTube': 'doc-badge--youtube',
    'Webinar': 'doc-badge--video',
    'Tutorial': 'doc-badge--video',
    
    // Images
    'Image': 'doc-badge--image',
    'JPEG': 'doc-badge--image',
    'PNG': 'doc-badge--image',
    'Screenshot': 'doc-badge--image',
    
    // Meeting Notes
    'Meeting Notes': 'doc-badge--meeting-notes',
    
    // Default
    'default': 'doc-badge--default'
  };

  return `doc-badge ${typeMap[contentType] || typeMap.default}`;
};
